import { useCart } from "../store/cart";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {open && (
        <div
          data-testid="cart-backdrop"
          className="fixed inset-0 z-50 bg-black/70"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[60] h-full w-full sm:w-[420px] bg-ink border-l border-white/20 transform transition-transform duration-200 ease-in ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/20">
          <div>
            <div className="font-anton text-2xl uppercase text-bone tracking-tighter">YOUR BAG</div>
            <div className="text-xs tracking-[0.2em] text-ash">// {items.length} ITEM{items.length !== 1 && "S"}</div>
          </div>
          <button data-testid="close-cart-button" onClick={() => setOpen(false)} aria-label="close" className="text-bone hover:text-blood">
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-220px)] divide-y divide-white/10">
          {items.length === 0 && (
            <div className="px-6 py-16 text-center">
              <p className="text-ash text-sm tracking-[0.2em] uppercase">// EMPTY //</p>
              <p className="font-marker text-blood text-2xl mt-4">no fear, no items.</p>
              <Link
                to="/shop"
                data-testid="empty-cart-shop-link"
                onClick={() => setOpen(false)}
                className="inline-block mt-6 px-6 py-3 bg-bone text-ink text-xs tracking-[0.2em] uppercase hover:bg-blood hover:text-bone transition-colors"
              >
                BROWSE DROPS
              </Link>
            </div>
          )}
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-3 px-6 py-4">
              <img src={it.image} alt={it.name} className="w-20 h-24 object-cover border border-white/10 bg-surface" />
              <div className="flex-1 min-w-0">
                <div className="font-anton text-sm uppercase tracking-tight text-bone truncate">{it.name}</div>
                <div className="text-[11px] tracking-[0.2em] text-ash mt-1">SIZE / {it.size}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`decrement-${it.key}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="w-7 h-7 border border-white/20 text-bone hover:bg-blood">
                    <Minus size={14} className="m-auto" />
                  </button>
                  <span className="font-mono text-sm text-bone w-6 text-center">{it.quantity}</span>
                  <button data-testid={`increment-${it.key}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="w-7 h-7 border border-white/20 text-bone hover:bg-blood">
                    <Plus size={14} className="m-auto" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <span className="font-mono text-bone text-sm">${(it.price * it.quantity).toFixed(2)}</span>
                <button data-testid={`remove-${it.key}`} onClick={() => removeItem(it.key)} className="text-ash hover:text-blood">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/20 px-6 py-5 bg-ink">
          <div className="flex justify-between font-mono text-bone mb-3">
            <span className="text-xs tracking-[0.2em] uppercase">SUBTOTAL</span>
            <span data-testid="cart-subtotal" className="text-lg font-anton">${subtotal.toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-ash mb-4">FREE SHIPPING OVER $100. NO REFUNDS. NO MERCY.</p>
          <Link
            to="/cart"
            data-testid="view-cart-checkout-link"
            onClick={() => setOpen(false)}
            className={`block w-full text-center px-6 py-4 text-xs tracking-[0.3em] uppercase font-mono transition-colors ${
              items.length === 0
                ? "bg-white/10 text-ash pointer-events-none"
                : "bg-blood text-bone hover:bg-bone hover:text-ink"
            }`}
          >
            CHECKOUT &nbsp;→
          </Link>
        </div>
      </aside>
    </>
  );
}
